<template>
  <div
    id="checkout"
    v-if="checkoutLoaded">
    <!-- Checkout Step Tabs -->
    <b-tabs
      pills
      v-model="tabIndex"
      nav-wrapper-class="card-header"
      nav-class="card-header-pills">
      <!-- Registrant Tabs -->
      <b-tab
        v-for="registration in registrations"
        :key="registration.id"
        :title="registration.registrant.full_name"
        class="container-fluid pb-3">
        <!-- Event Details -->
        <div
          class="row border-bottom border-muted py-3">
          <div class="
            col
            d-flex
            flex-column
            flex-lg-row
            align-items-center">
            <h3 class="mb-3 m-lg-0">
              Register {{registration.registrant.full_name}} for {{event.org_name}} - {{registration.event.name}}
            </h3>
          </div>
        </div>
        <!-- / Event Details -->

        <!-- Registration Form -->
        <div class="row mt-3">
          <form
            id="registration-form"
            name="registrationForm"
            class="col"
            @submit.prevent="submitRegistrationData(eventForms[registration.event.id].form_fields, registration)">
            <!-- Form Fields -->
            <FormFields
              :form-fields="orderedFields(eventForms[registration.event.id].form_fields)"
              v-model="registration.form_data"
              hide-buttons
            />
            <!-- / Form Fields -->

            <!-- Form Buttons -->
            <div class="row justify-content-end mt-3">
              <div class="col-lg-auto">
                <b-button
                  class="w-100 w-lg-auto"
                  type="submit"
                  variant="success"
                  :disabled="submitting">
                  Continue
                </b-button>
              </div>
            </div>
            <!-- / Form Buttons -->
          </form>
        </div>
        <!-- / Registration Form -->
      </b-tab>
      <!-- / Registrant Tabs -->

      <!-- Payment Tab -->
      <b-tab
        :title="isZeroDollarCart && !cartEmpty ? 'Confirm Registrations' : 'Payment'"
        :disabled="anyFormInvalid"
        :title-link-class="{ 'no-pointer-events': isZeroDollarCart && !cartEmpty }"
        class="container-fluid pb-3">
          <div class="row mt-3">
            <CartSummary
              class="col"
              @remove-registration="removeReg"
            />
          </div>
          <div class="row border-bottom border-muted py-3">
            <div class="
              col
              d-flex
              flex-column
              flex-lg-row
              align-items-center">
              <h3 class="mb-3 m-lg-0">
                Total: {{registrationSession.amount_to_charge | currency}}
              </h3>

              <div class="flex-fill d-xs-none d-lg-block"></div>

              <!-- Payment Request Button -->
              <button
                id="stripe-payment-request-mount"
                class="stripe-payment-request"
                v-if="paymentRequestStatus !== null && !isZeroDollarCart && !cartEmpty"
                type="button">
              </button>
              <!-- / Payment Request Button -->

              <!-- Credit Card Fallback -->
              <div
                id="stripe-checkout"
                class="mt-3 mt-lg-0"
                v-if="!isZeroDollarCart && !cartEmpty">
                <b-button
                  class="ml-lg-3"
                  variant="success"
                  type="button"
                  :disabled="cartEmpty || submitting"
                  @click="checkoutStart">
                  Pay With Credit Card
                </b-button>
              </div>
              <!-- / Credit Card Fallback -->

              <!-- Partial Payment -->
              <b-button
                v-if="registrationsAllowingPartialPayments.length > 0 && !isZeroDollarCart && !cartEmpty"
                class="ml-lg-3 mt-3 mt-lg-0"
                variant="success"
                type="button"
                @click="showPartialPaymentModal"
                :disabled="submitting">
                Make Partial Payment{{registrationsAllowingPartialPayments.length > 1 ? 's' : ''}}
              </b-button>
              <!-- / Partial Payment -->

              <!-- Confirm Waitlist Only Registrations -->
              <b-button
                v-if="isZeroDollarCart && !cartEmpty"
                class="ml-lg-3 mt-3 mt-lg-0"
                variant="success"
                type="button"
                @click="confirmRegistrations"
                :disabled="submitting">
                Complete Registration Without Charge 
              </b-button>
              <!-- / Confirm Waitlist Only Registrations -->
            </div>
          </div>
      </b-tab>
      <!-- / Payment Tab -->
    </b-tabs>
    <!-- / Checkout Step Tabs -->

    <!-- Partial Payments Modal -->
    <b-modal
      id="partial-payments-modal"
      ref="partialPaymentsModal"
      title="Partial Payments"
      hide-footer
      size="lg"
      @shown="mountPartialPaymentPaymentRequestButton"
      @hide="checkDirtyModalForm($event, isPartialPaymentsDirty)"
      @hidden="resetPartialPaymentsForm(); unmountPartialPaymentPaymentRequestButton()"
      v-model="partialPaymentsModalOpen">
      <b-form
        id="partial-payments-form"
        name="partialPaymentsForm"
        @submit.prevent="partialPaymentsCheckoutStart">
        <!-- Repeat Over ALL Registrations -->
        <div
          v-for="registration in orderedRegistrations"
          :key="registration.id"
          class="row mb-3">

          <!-- Registration -->
          <div class="col-lg">
            <label :for="`partial-payments-${registration.id}`">
              {{registration.event.name}} - {{registration.registrant.full_name}}
              <small class="text-danger">
                <div v-if="registration.event.partial_payments_open && registration.event.registration_open">
                  Enter an amount between
                  {{registration.cost.calc_minimum_payment | currency}}
                  and
                  {{registration.cost.calc_amount_total | currency}}.
                </div>
                <div v-else-if="!registration.event.registration_open">
                  Waitlist - {{registration.cost.calc_amount_total | currency}} Payment Due Upon Arrival.
                </div>
                <div v-else>Partial Payment not accepted for this event.</div>
              </small>
            </label>
          </div>
          <!-- / Registration -->

          <!-- Payment -->
          <div class="col-lg-auto partial-payment-entry">
            <!-- Input for registration allowing PP -->
            <Number
              v-if="registration.event.partial_payments_open && registration.event.registration_open"
              class="enter-payment"
              placeholder="0.00"
              type="currency"
              :id="`partial-payments-${registration.id}`"
              :name="`partialPayments${registration.id}`"
              :min="registration.cost.calc_minimum_payment"
              :max="registration.cost.calc_amount_total"
              :disabled="!!partialPaymentsLoadingMsg"
              v-model="partialPayments[registration.id]"
            />
            <!-- / Input for registration allowing PP -->

            <!-- Non-PP total amount display -->
            <Number
              v-else
              class="display-payment"
              :id="`partial-payments-${registration.id}`"
              :name="`partialPayments${registration.id}`"
              type="currency"
              :value="registration.cost.amount_to_charge"
              disabled
            />
            <!-- / Non-PP total amount display -->
          </div>
          <!-- / Payment -->
        </div>
        <!-- / Repeat Over ALL Registrations -->

        <hr>

        <!-- Total -->
        <div class="d-flex align-items-center justify-content-end">
          <label class="m-0 mr-2 mt-1">
            Total:
          </label>
          <div class="partial-payment-entry">
            <Number
              class="display-payment"
              type="currency"
              :value="totalWithPartialPayments"
              disabled
            />
          </div>
        </div>
        <!-- / Total -->

        <!-- Form Buttons -->
        <div class="row justify-content-end mt-4">
          <!-- Payment Request Button -->
          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <button
              id="stripe-payment-request-pp-mount"
              class="stripe-payment-request w-100 w-lg-auto"
              type="button"
              :disabled="!!partialPaymentsLoadingMsg || totalWithPartialPayments < minimumTotalPayment || submitting">
            </button>
          </div>
          <!-- / Payment Request Button -->

          <div class="col-lg-auto pr-lg-0 mt-lg-0 mt-3">
            <b-button
              type="submit"
              class="w-100 w-lg-auto"
              variant="success"
              :disabled="!!partialPaymentsLoadingMsg || totalWithPartialPayments < minimumTotalPayment || submitting">
              Pay With Credit Card
            </b-button>
          </div>

          <div class="col-lg-auto mt-lg-0 mt-3">
            <b-button
              class="w-100 w-lg-auto"
              type="button"
              variant="danger"
              :disabled="!!partialPaymentsLoadingMsg || submitting"
              @click="partialPaymentsModalOpen = false;">
              Cancel
            </b-button>
          </div>
        </div>
        <!-- / Form Buttons -->
      </b-form>

      <!-- Loading Indicator -->
      <Loader
        id="partial-payments-loading"
        variant="light"
        :msg="partialPaymentsLoadingMsg">
      </Loader>
      <!-- / Loading Indicator -->
    </b-modal>
    <!-- / Partial Payments Modal -->

    <!-- Stripe Checkout -->
    <vue-stripe-checkout
      ref="checkoutRef"
      image="https://stripe.com/img/documentation/checkout/marketplace.png"
      name="KJK Registrations"
      :description="`${event.org_name}: ${event.name}`"
      :allow-remember-me="false"
      :amount="checkoutOptions.amount"
      @done="checkoutOptions.done"
      @canceled="checkoutOptions.canceled">
    </vue-stripe-checkout>
    <!-- / Stripe Checkout -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import AppMixin from '../services/mixin.app';
import CartSummary from '../components/CartSummary.vue';
import Number from '../components/Number.vue';
import DirtyForm from '../services/dirty-form';

// eslint-disable-next-line
let stripe;
let elements;
let paymentRequest;
let prButton;

export default {
  name: 'checkout',
  mixins: [
    AppMixin,
    DirtyForm,
  ],
  components: {
    CartSummary,
    Number,
  },
  data() {
    return {
      checkoutLoaded: false,
      tabIndex: 0,
      eventForms: {},
      paymentRequestStatus: null,
      paymentRequestButtonMounted: false,
      paymentRequestButtonMountedPartialPayments: false,
      paymentProcessing: false,
      submitting: false,

      // partial payments
      partialPaymentsModalOpen: false,
      partialPayments: {},
      partialPaymentsCopy: {},
      partialPaymentsLoadingMsg: '',

      // checkout
      checkoutOptions: {
        amount: 0,
        done: () => {},
        canceled: () => {},
      },
    };
  },
  watch: {
    tabIndex(index) {
      if (!this.cartEmpty
        && index === this.registrations.length
        && this.paymentRequestStatus !== null
        && !this.paymentRequestButtonMounted
        && !this.isZeroDollarCart) {
        // mount normal pr button
        prButton.unmount();
        prButton.mount('#stripe-payment-request-mount');
        this.paymentRequestButtonMounted = true;
      }
    },
    partialPaymentsCostInCents(cost) {
      if (this.partialPaymentsModalOpen) {
        this.updatePaymentRequestAmount(cost);
      }
    },
  },
  computed: {
    costInCents() {
      return this.convertFloatDollarsToIntegerCents(this.registrationSession.amount_to_charge);
    },
    registrations() {
      return _(this.registrationSession.registrations)
        .orderBy('registrant.full_name')
        .reject(r => this.eventForms[r.event.id].form_fields.length === 0)
        .value();
    },
    anyFormInvalid() {
      let anyInvalid = false;

      this.registrations.forEach((registration) => {
        const missingFields = this.getMissingFieldsForRegistration(
          this.eventForms[registration.event.id].form_fields,
          registration.form_data,
        );

        if (!anyInvalid && missingFields.length > 0) {
          anyInvalid = true;
        }
      });

      return anyInvalid;
    },

    // partial payments
    orderedRegistrations() {
      return _.orderBy(
        this.registrationSession.registrations,
        r => r.event.partial_payments_open && r.event.registration_open,
        'desc',
      );
    },
    registrationsAllowingPartialPayments() {
      return _.filter(
        this.registrationSession.registrations,
        r => r.event.partial_payments_open && r.event.registration_open,
      );
    },
    isPartialPaymentsDirty() {
      const partialPaymentsJSON = JSON.stringify(this.partialPayments);
      const partialPaymentsCopyJSON = JSON.stringify(this.partialPaymentsCopy);
      return partialPaymentsJSON !== partialPaymentsCopyJSON;
    },
    totalWithPartialPayments() {
      let sum = 0;

      _.each(this.registrationSession.registrations, (r) => {
        let cost = this.convertFloatDollarsToIntegerCents(r.cost.amount_to_charge);

        if (r.event.partial_payments_open) {
          if (isNaN(this.partialPayments[r.id])) {
            cost = this.convertFloatDollarsToIntegerCents(r.cost.calc_minimum_payment);
          } else {
            cost = this.convertFloatDollarsToIntegerCents((this.partialPayments[r.id]));
          }
        }

        sum += cost;
      });

      return sum / 100;
    },
    partialPaymentsCostInCents() {
      return this.convertFloatDollarsToIntegerCents(this.totalWithPartialPayments);
    },
    minimumTotalPayment() {
      let sum = 0;

      _.each(this.registrationSession.registrations, (r) => {
        let cost = this.convertFloatDollarsToIntegerCents((r.cost.amount_to_charge));

        if (r.event.partial_payments_open) {
          cost = this.convertFloatDollarsToIntegerCents(r.cost.calc_minimum_payment);
        }

        sum += cost;
      });

      return sum / 100;
    },
  },
  methods: {
    next() {
      const next = this.tabIndex + 1;
      this.$nextTick(() => {
        this.tabIndex = next;
      });
    },
    completeCharge(token, partialPayments) {
      const { uuid } = this.registrationSession;
      return DataSvc.createCharge(uuid, token, partialPayments)
        .catch(this.formDataAPIValidationError)
        .then(() => {
          this.updateActiveRegistrationSession();
          return this.updateClosedRegistrationSessions();
        })
        .then(() => {
          // go to confirmation screen
          this.$router.push({
            name: 'confirmation',
            params: { uuid },
          });
        });
    },
    checkoutStart() {
      this.submitting = true;
      this.showGlobalLoader('Processing...');

      this.checkCostScenario(() => {
        // set options
        this.checkoutOptions = {
          amount: this.costInCents,
          done: this.checkoutDone,
          canceled: this.hideGlobalLoader,
        };

        this.showGlobalLoader(true);
        this.$nextTick(this.$refs.checkoutRef.open);
      }).finally(() => {
        this.submitting = false;
        this.hideGlobalLoader();
      });
    },
    checkoutDone({ token }) {
      this.showGlobalLoader('Processing payment...');
      this.completeCharge(token.id)
        .finally(this.hideGlobalLoader);
    },
    orderedFields(fields) {
      return _.orderBy(fields, 'order');
    },
    getMissingFieldsForRegistration(fields, data) {
      const requiredFields = _.filter(fields, 'required');

      if (!data) return requiredFields;

      return _.filter(requiredFields, (field) => {
        let enteredValue = data[field.name];

        if (typeof enteredValue === 'string') {
          enteredValue = enteredValue.trim();
        }

        return !enteredValue;
      });
    },
    submitRegistrationData(fields, registration) {
      const missingFields = _(this.getMissingFieldsForRegistration(fields, registration.form_data))
        .map(field => `&bull; ${field.label}`)
        .join('<br>');

      if (missingFields.length > 0) {
        return this.showWarning(
          `The following required fields are missing:<br><br>${missingFields}`,
          'Invalid Registration',
        );
      }

      this.showGlobalLoader('Saving registration...');
      this.submitting = true;

      return DataSvc.updateRegistration(
        this.registrationSession.uuid,
        registration,
      ).catch(this.formDataAPIValidationError)
        .then(this.updateActiveRegistrationSession)
        .then(this.next)
        .finally(() => {
          this.hideGlobalLoader();
          this.submitting = false;
        });
    },
    formDataAPIValidationError(error) {
      // if error is due to bad registrant form data
      // send back to specific registrant form step
      if (error.status === 402) {
        const [firstRegistrantInError] = _(error.data.errors)
          .map(e => e.message.split(':')[0])
          .uniq()
          .sortBy()
          .value();

        let registrantIndex = _.findIndex(
          this.registrations,
          r => r.registrant.full_name === firstRegistrantInError,
        );

        if (registrantIndex === -1) {
          registrantIndex = 0;
        }

        this.tabIndex = registrantIndex;
      }

      return Promise.reject();
    },
    removeReg(id) {
      return this.removeRegistration(id)
        .then(() => {
          // if no more registrations, go to cart
          if (this.cartEmpty) {
            this.goToCart();
          } else {
            this.updatePaymentRequestAmount(this.costInCents);
          }
        });
    },
    updatePaymentRequestAmount(costInCents) {
      if (this.paymentRequestStatus !== null
        && !this.paymentProcessing
        && !this.cartEmpty) {
        paymentRequest.update({
          total: {
            label: 'KJK Registrations',
            amount: costInCents,
          },
        });
      }
    },

    // partial payments
    showPartialPaymentModal() {
      this.submitting = true;
      this.showGlobalLoader('Processing...');

      this.checkCostScenario(() => {
        this.partialPaymentsModalOpen = true;

        _.each(this.registrationSession.registrations, (r) => {
          if (r.event.partial_payments_open) {
            this.partialPayments[r.id] = r.cost.calc_minimum_payment;
            this.partialPaymentsCopy[r.id] = r.cost.calc_minimum_payment;
          }
        });

        this.updatePaymentRequestAmount(this.partialPaymentsCostInCents);
      }).finally(() => {
        this.submitting = false;
        this.hideGlobalLoader();
      });
    },
    resetPartialPaymentsForm() {
      this.partialPayments = {};
      this.partialPaymentsCopy = {};

      // reset payment request about back to total price
      this.updatePaymentRequestAmount(this.costInCents);
    },
    partialPaymentsCheckoutStart() {
      this.submitting = true;

      this.checkCostScenario(() => {
        // set options
        this.checkoutOptions = {
          amount: this.partialPaymentsCostInCents,
          done: this.partialPaymentsCheckoutDone,
          canceled: this.partialPaymentsCheckoutCanceled,
        };

        this.partialPaymentsLoadingMsg = true;
        this.$nextTick(this.$refs.checkoutRef.open);
      }).finally(() => {
        this.submitting = false;
      });
    },
    partialPaymentsCheckoutDone({ token }) {
      this.partialPaymentsLoadingMsg = 'Processing payment...';
      this.completeCharge(token.id, this.partialPayments)
        .then(() => {
          this.partialPaymentsModalOpen = false;
        })
        .finally(() => {
          this.partialPaymentsLoadingMsg = '';
        });
    },
    partialPaymentsCheckoutCanceled() {
      this.partialPaymentsLoadingMsg = '';
    },
    mountPartialPaymentPaymentRequestButton() {
      if (this.paymentRequestStatus !== null
        && !this.paymentRequestButtonMountedPartialPayments) {
        // umount normal pr button
        prButton.unmount();
        this.paymentRequestButtonMounted = false;

        // mount pp pr button
        prButton.mount('#stripe-payment-request-pp-mount');
        this.paymentRequestButtonMountedPartialPayments = true;
      }
    },
    unmountPartialPaymentPaymentRequestButton() {
      if (this.paymentRequestStatus !== null
        && this.paymentRequestButtonMountedPartialPayments
        && !this.paymentRequestButtonMounted) {
        // unmount pp pr button
        prButton.unmount();
        this.paymentRequestButtonMountedPartialPayments = false;

        // mount normal pr button
        prButton.mount('#stripe-payment-request-mount');
        this.paymentRequestButtonMounted = true;
      }
    },

    // no payment
    confirmRegistrations() {
      this.submitting = true;
      this.showGlobalLoader('Processing...');

      this.checkCostScenario(this.completeCharge)
        .finally(() => {
          this.submitting = false;
          this.hideGlobalLoader();
        });
    },
  },
  destroyed() {
    prButton.unmount();
    prButton.destroy();
  },
  created() {
    const initCheckout = () => {
      let validateCostPromise = Promise.resolve();

      // stripe init
      // eslint-disable-next-line
      stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_API_KEY);
      elements = stripe.elements();

      if (this.event.id
          && !this.cartEmpty
          && !this.failedRegistration) {
        const formPromises = [];
        const eventIDs = _(this.registrationSession.registrations)
          .map('event.id')
          .uniq()
          .value();

        // get each event's registration form
        _.each(eventIDs, (eventID) => {
          const fp = DataSvc.getEventForms(
            this.registrationSession.uuid,
            eventID,
          ).then((forms) => {
            // filter out hidden form_fields
            const form = _.first(forms);
            form.form_fields = _.reject(form.form_fields, { hidden: true });
            this.eventForms[eventID] = form;
          });

          formPromises.push(fp);
        });

        this.showGlobalLoader('Loading checkout...');

        Promise.all(formPromises)
          .then(() => {
            this.checkoutLoaded = true;
          }, () => {
            this.showGlobalLoader('There was an error loading checkout.');
          })
          .finally(this.hideGlobalLoader);

        // create payment request instance with current cost
        paymentRequest = stripe.paymentRequest({
          country: 'US',
          currency: 'usd',
          total: {
            label: 'KJK Registrations',
            amount: this.costInCents,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        });

        // mount payment request button if possible
        paymentRequest.canMakePayment()
          .then((status) => {
            this.paymentRequestStatus = status;

            // if enabled listen to events & create button but mount later
            if (status !== null) {
              // listen for payment request complete and create charge for token
              paymentRequest.on('token', (e) => {
                this.partialPaymentsModalOpen = false;
                this.paymentProcessing = true;
                this.showGlobalLoader('Processing payment...');

                // complete the charge
                validateCostPromise
                  .then(() => this.completeCharge(e.token.id, this.partialPayments), () => e.complete('fail'))
                  .then(() => e.complete('success'), () => e.complete('fail'))
                  .finally(() => {
                    this.hideGlobalLoader();
                    this.paymentProcessing = false;
                  });
              });

              // listen for payment request canceled event
              paymentRequest.on('cancel', () => {
                this.paymentProcessing = false;
                this.partialPaymentsLoadingMsg = false;
                this.hideGlobalLoader();
              });

              try {
                // create payment request button stripe element
                prButton = elements.create('paymentRequestButton', {
                  paymentRequest,
                  style: {
                    paymentRequestButton: {
                      height: '38px',
                    },
                  },
                });
              } catch (e) {
                if (e.message !== 'Can only create one Element of type paymentRequestButton.') {
                  // eslint-disable-next-line
                  console.error(e);
                }
              }

              prButton.on('click', () => {
                // if doing partial payments
                if (this.partialPaymentsModalOpen) {
                  this.partialPaymentsLoadingMsg = true;
                } else {
                  this.showGlobalLoader(true);
                }

                validateCostPromise = this.checkCostScenario();
              });
            }
          });
      } else if (this.event.id && this.cartEmpty) {
        this.goToEvent();
      } else {
        this.$router.replace({
          name: 'not-found',
        });
      }
    };

    this.initPromise.then(initCheckout);
  },
};
</script>

<style lang="scss">
#checkout {
  .tabs {
    .nav {
      .nav-item {
        a {
          // pointer-events: none;
        }
      }
    }

    .tab-panel {
      outline: none;
    }
  }

  .no-pointer-events {
    pointer-events: none;
    cursor: default !important;
  }
}
</style>
